import React from "react";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import "moment/locale/ko";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint, faSpinner } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

import API from "../services/API";
import Constants, { INVENTORY_ADJUST_TYPES } from "../helpers/Constants";
import {
  GetInventoryTransactionReason,
  LimitExcessModal,
  LoadingElement,
  PeriodOptions,
} from "./Elements";
import {
  DSButton,
  DSDateInput,
  DSDropdown,
  DSFormRow,
  DSModal,
  DSPagination,
  DSQuickDateGroup,
  DSSearchInput,
  DSTable,
  DSTextInput,
} from "./ds";
import {
  isAvailableAmount,
  isAvailableQuantity,
  isValidDate,
  numberToCurrency,
  numberWithCommas,
  removeString,
  isLegacyInventoryHistories,
  getDateShortRange,
} from "../helpers/Utils";
import ReportMailModal from "./ReportMailModal";
import * as reducers from "../reducers";
import {
  getItems,
  getInventoryHistoriesByStore,
  getOrder,
  getInventoryHistoriesByGroupId,
  shareHistoryPdf,
  getPurchaseOrderById,
  getSuppliers,
  updateInventoryHistory,
  refreshInventoryHistory,
  downloadInventoryHistories,
  shareGroupHistoryPdf,
  updateInventoryHistoryNote,
  clearInventoryHistories,
} from "../actions";
import Authority from "../services/Authority";
import Analytics from "../services/Analytics";
import { getOrderNumber } from "../reducers/order";
import cafe24LogoHorizontal from "../img/cafe24/cafe24_logo_horizontal.png";
import StockReportDownloadModal from "./StockReportDownloadModal";
import {
  requestReportDownload,
  REPORT_RESULT,
} from "../helpers/reportDownload";

const ITEM_PER_PAGE = 30;

export const TABLE_HEADERS = [
  {
    label: "일시",
    width: "15%",
    key: "createdAt",
  },
  {
    label: "위치",
    width: "10%",
    key: "location",
  },
  {
    label: "제품",
    width: "15%",
    key: "prodName",
  },
  {
    label: "하위제품",
    width: "8%",
    key: "skuName",
  },
  {
    label: "품목코드(SKU)",
    width: "12%",
    key: "model",
  },
  {
    label: "조정 타입",
    width: "10%",
    key: "type",
  },
  {
    label: "조정 수량",
    width: "10%",
    key: "quantity",
    isQty: true,
    func: numberWithCommas,
  },
  {
    label: "총 비용",
    width: "10%",
    key: "totalCost",
    isAmount: true,
    func: numberToCurrency,
  },
  {
    label: "노트",
    width: "10%",
    key: "note",
  },
];

export const TABLE_GROUP_HEADERS = [
  {
    label: "일시",
    width: "20%",
    key: "createdAt",
  },
  {
    label: "위치",
    width: "15%",
    key: "location",
  },
  {
    label: "조정타입",
    width: "15%",
    key: "type",
  },
  {
    label: "조정 제품수",
    width: "10%",
    key: "skuLength",
  },
  {
    label: "총 비용",
    width: "15%",
    key: "totalCost",
    isAmount: true,
    func: numberToCurrency,
  },
  {
    label: "노트",
    width: "25%",
    key: "note",
  },
];

const TABLE_HEADER_IN_HISTORY_MODAL = [
  {
    label: "제품",
    width: "23%",
    key: "prodName",
  },
  {
    label: "하위제품",
    width: "22%",
    key: "skuName",
  },
  {
    label: "품목코드(SKU)",
    width: "15%",
    key: "model",
  },
  {
    label: "조정 타입",
    width: "15%",
    key: "reason",
  },
  {
    label: "조정 수량",
    width: "10%",
    key: "quantity",
    func: numberWithCommas,
  },
  {
    label: "비용",
    width: "15%",
    key: "cost",
    isAmount: true,
    func: numberToCurrency,
  },
];

const HistoryLabel = React.memo(({ label, value }) => (
  <div style={{ display: "flex", gap: 8, padding: "5px 0" }}>
    <div
      className="ds-t-body-s ds-fc-secondary"
      style={{ width: "30%", flexShrink: 0 }}
    >
      {label}
    </div>
    <div className="ds-t-body-s" style={{ width: "70%" }}>
      {value}
    </div>
  </div>
));

// 조정 타입(원본 reason) → ds-reason-badge 모디파이어 매핑
// 우진 확정(2026-07-15): 모든 칩은 판매와 동일한 중립 fill(기본값), 지점이동만 옅은 그린 fill
const REASON_BADGE_CLASS = {
  transfer: "ds-reason-badge--green",
};

const ReasonBadge = React.memo(({ label, reason }) => (
  <span
    className={`ds-reason-badge ${
      REASON_BADGE_CLASS[reason] || "ds-reason-badge--neutral"
    }`}
  >
    {label}
  </span>
));

// 기존 CustomCell 규칙 유지: func 포맷터, 수량/금액 셀은 부호에 따라 색상(양수=accent, 음수=error)
const renderHistoryCellValue = (header, item) => {
  const value = item[header.key];
  if (header.key === "type" && item.originalType) {
    return <ReasonBadge label={value} reason={item.originalType} />;
  }
  const display = header.func && value ? header.func(value) : value;
  if (header.isQty || header.isAmount || header.key === "quantity") {
    const color =
      value > 0
        ? "var(--ds-text-accent)"
        : value < 0
          ? "var(--ds-system-error)"
          : undefined;
    return <span style={color ? { color } : undefined}>{display}</span>;
  }
  return display;
};

const HistoryTable = React.memo(({ headers, items, onRowClick }) => (
  <DSTable>
    <DSTable.Head>
      {headers.map((header) => (
        <DSTable.Th key={header.key} width={header.width}>
          {header.label}
        </DSTable.Th>
      ))}
    </DSTable.Head>
    <DSTable.Body>
      {(items || []).length > 0 ? (
        items.map((item, index) => (
          <DSTable.Row
            key={item.id || index}
            onClick={onRowClick ? () => onRowClick(item) : undefined}
            style={onRowClick ? { cursor: "pointer" } : undefined}
          >
            {headers.map((header) => (
              <DSTable.Td key={header.key}>
                {renderHistoryCellValue(header, item)}
              </DSTable.Td>
            ))}
          </DSTable.Row>
        ))
      ) : (
        <DSTable.Row>
          <DSTable.Td colSpan={headers.length} centered>
            {"조회된 데이터가 없습니다 :("}
          </DSTable.Td>
        </DSTable.Row>
      )}
    </DSTable.Body>
  </DSTable>
));


const getInventoryReasonOptions = (hasOnlineStock) => {
  if (!hasOnlineStock) {
    return INVENTORY_ADJUST_TYPES;
  }

  const salesIndex = INVENTORY_ADJUST_TYPES.findIndex(
    (t) => t.value === "sales",
  );
  let reasons =
    salesIndex > -1
      ? [
          ...INVENTORY_ADJUST_TYPES.slice(0, salesIndex + 1),
          { label: "판매(카페24)", value: "cafe24sales" },
          ...INVENTORY_ADJUST_TYPES.slice(salesIndex + 1),
        ]
      : [...INVENTORY_ADJUST_TYPES];

  const returnsIndex = reasons.findIndex((t) => t.value === "returns");
  if (returnsIndex > -1) {
    reasons = [
      ...reasons.slice(0, returnsIndex + 1),
      { label: "반품입고(카페24)", value: "cafe24Returns" },
      ...reasons.slice(returnsIndex + 1),
    ];
  }
  return reasons;
};

const HistoryDetailSwitch = React.memo(
  ({
    history,
    ordersById,
    suppliersById,
    currentLocationId,
    purchaseOrdersById,
  }) => {
    switch (history.originalType) {
      case "sales":
      case "returns":
      case "exchange":
        const order = ordersById[history.original.referId];
        return (
          <>
            <div
              style={{
                borderTop: "1px solid var(--ds-border-default)",
                margin: "16px 0",
              }}
            />
            <div>
              <div className="ds-t-body-m" style={{ marginBottom: 4 }}>
                {"관련 주문내역"}
              </div>
              {order ? (
                <>
                  <HistoryLabel
                    label="주문일시"
                    value={moment(order.createdAt).format("YYYY/MM/DD, HH:mm")}
                  />
                  <HistoryLabel
                    label="주문번호"
                    value={`#${getOrderNumber(
                      order.id,
                      order.number,
                      order.numberPrefix,
                      order.originId,
                    )}`}
                  />
                  <HistoryLabel
                    label="결제금액"
                    value={numberToCurrency(order.amountPaid || order.amount)}
                  />
                </>
              ) : null}
            </div>
          </>
        );
      case "newStock":
        switch (!_.isEmpty(history.original.purchaseOrderId)) {
          case true:
            const _purchaseOrder =
              purchaseOrdersById[history.original.purchaseOrderId];
            const remoteSupplier = _purchaseOrder.supplierId
              ? suppliersById[_purchaseOrder.supplierId]?.name
              : currentLocationId === _purchaseOrder.createdLocationId
                ? "(삭제된 거래처)"
                : "(타지점 거래처)";
            return (
              <>
                <div
                  style={{
                    borderTop: "1px solid var(--ds-border-default)",
                    margin: "16px 0",
                  }}
                />
                <div>
                  <div className="ds-t-body-m" style={{ marginBottom: 4 }}>
                    {"관련 발주서"}
                  </div>
                  <HistoryLabel
                    label="발주일시"
                    value={moment(_purchaseOrder.createdAt).format(
                      "YYYY/MM/DD, HH:mm",
                    )}
                  />
                  <HistoryLabel
                    label="발주번호"
                    value={`#${_purchaseOrder.number}`}
                  />
                  {_purchaseOrder.supplierOrigin === "supplier" && (
                    <HistoryLabel label="거래처" value={remoteSupplier} />
                  )}
                </div>
              </>
            );
          case false:
          default:
            return null;
        }

      default:
        return null;
    }
  },
);

const HistoryModal = React.memo(
  ({
    open,
    onCancel,
    history,
    isGroup,
    onPrintPress,
    histories,
    brushupDetailItems,
    detailProps,
    hasMoreItems,
    isPaging,
    loadMore,
    getEditableState,
    isLoading,
    onExpiryDateChange,
    onForceSave,
    hasExpiry,
    onCostChange,
    onQuantityChange,
    forceCost,
    forceQuantity,
    forceExpiryDate,
    isRetail,
    isPrinting,
    isHistorySavable,
    onNoteChange,
    onNoteSave,
    tempNote,
  }) => {
    switch (isGroup) {
      case true:
        let items = brushupDetailItems(histories);
        return (
          <DSModal open={open} onClose={onCancel} title="조정내역" width={960}>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                padding: 24,
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div>
                <DSButton
                  variant="primary"
                  size="sm"
                  disabled={isPrinting}
                  onClick={() => onPrintPress(history.id)}
                  icon={
                    <FontAwesomeIcon
                      icon={isPrinting ? faSpinner : faPrint}
                      spin={isPrinting}
                    />
                  }
                />
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 24 }}>
                <div style={{ width: "50%" }}>
                  <HistoryLabel label="조정일시" value={history.createdAt} />
                  <HistoryLabel label="담당자" value={history.employee} />
                  <HistoryLabel label="조정지점" value={history.location} />
                  <HistoryLabel label="조정제품 수" value={history.skuLength} />
                  <HistoryLabel
                    label="총 비용"
                    value={numberToCurrency(history.totalCost)}
                  />
                </div>
                <div style={{ width: "50%" }}>
                  <div className="ds-t-body-s" style={{ marginBottom: 8 }}>
                    {"조정노트"}
                  </div>
                  <DSTextInput
                    multiline
                    rows={5}
                    value={history.note || ""}
                    onChange={() => {}}
                    readOnly
                  />
                </div>
              </div>
              <HistoryTable
                items={items}
                headers={TABLE_HEADER_IN_HISTORY_MODAL}
              />
              {hasMoreItems || isPaging ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <DSButton
                    variant="secondary"
                    onClick={loadMore}
                    disabled={isPaging}
                    icon={
                      isPaging ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : null
                    }
                  >
                    {"더보기"}
                  </DSButton>
                </div>
              ) : null}
            </div>
          </DSModal>
        );
      case false:
      default:
        let editable = getEditableState(
          history.originalType,
          history.totalCost,
          history.purchaseOrderId,
        );
        const isEditable =
          isRetail &&
          (editable.quantity || editable.cost || editable.expiryDate);
        return (
          <DSModal
            open={open}
            onClose={onCancel}
            title="조정내역"
            width={640}
            overflowVisible
          >
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                padding: 24,
              }}
            >
              <HistoryLabel label="조정일시" value={history.createdAt} />
              <HistoryLabel label="제품이름" value={history.prodName} />
              <HistoryLabel label="하위제품 이름" value={history.skuName} />
              <HistoryLabel label="바코드" value={history.unit || "-"} />
              <HistoryLabel
                label="품목코드(SKU)"
                value={history.model || "-"}
              />
              {history.original.locationId ? (
                <HistoryLabel label="조정지점" value={history.location} />
              ) : null}
              <HistoryLabel
                label="조정사유"
                value={
                  <ReasonBadge
                    label={history.type}
                    reason={history.originalType}
                  />
                }
              />
              <HistoryLabel label="조정수량" value={history.quantity} />
              <HistoryLabel label="담당자" value={history.employee || "-"} />
              <HistoryLabel
                label="총 비용"
                value={numberToCurrency(history.totalCost)}
              />
              <HistoryLabel
                label="소비기한(유통기한)"
                value={history.expiryDate || "-"}
              />
              <HistoryDetailSwitch history={history} {...detailProps} />
              <div
                style={{
                  borderTop: "1px solid var(--ds-border-default)",
                  margin: "16px 0",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                <div className="ds-t-body-s">{"노트"}</div>
                <DSButton
                  variant="secondary"
                  size="sm"
                  disabled={isLoading}
                  onClick={onNoteSave}
                  icon={
                    isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : null
                  }
                >
                  {"저장하기"}
                </DSButton>
              </div>
              <div style={{ marginTop: 8 }}>
                <DSTextInput
                  multiline
                  rows={4}
                  value={tempNote || ""}
                  onChange={(v) => onNoteChange({ target: { value: v } })}
                />
              </div>
              {isEditable ? (
                <>
                  <div
                    style={{
                      borderTop: "1px solid var(--ds-border-default)",
                      margin: "16px 0",
                    }}
                  />
                  <div
                    className="ds-t-body-s ds-fc-accent"
                    style={{ marginBottom: 12 }}
                  >
                    {"조정된 내역은 리포트와 이전/이후 재고현황에 반영됩니다."}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    {editable.quantity ? (
                      <DSFormRow label="수량조정">
                        <DSTextInput
                          type="text"
                          placeholder={"수량 임의조정"}
                          value={
                            forceQuantity != null
                              ? forceQuantity.toString()
                              : history.quantity != null
                                ? String(history.quantity)
                                : ""
                          }
                          onChange={(v) =>
                            onQuantityChange({ target: { value: v } })
                          }
                        />
                      </DSFormRow>
                    ) : null}
                    {editable.cost ? (
                      <DSFormRow label="단가">
                        <DSTextInput
                          type="text"
                          placeholder={"단가 임의조정"}
                          value={forceCost != null ? String(forceCost) : ""}
                          onChange={(v) =>
                            onCostChange({ target: { value: v } })
                          }
                        />
                      </DSFormRow>
                    ) : null}
                    {hasExpiry && editable.expiryDate ? (
                      <DSFormRow label="소비기한(유통기한)">
                        <DSDateInput
                          label="날짜"
                          value={
                            forceExpiryDate || history.originExpiryDate
                              ? moment(
                                  forceExpiryDate || history.originExpiryDate,
                                ).toDate()
                              : null
                          }
                          onChange={(date) => {
                            if (date) {
                              onExpiryDateChange(date);
                            }
                          }}
                        />
                      </DSFormRow>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
            {isEditable ? (
              <div
                style={{
                  padding: 16,
                  borderTop: "1px solid var(--ds-border-default)",
                }}
              >
                <DSButton
                  variant="primary"
                  size="lg"
                  style={{ width: "100%" }}
                  onClick={() => onForceSave(history.id)}
                  icon={
                    isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : null
                  }
                  disabled={
                    isLoading ||
                    !Authority.can(
                      Authority.actions.update,
                      Authority.subjects.stock,
                    ) ||
                    !isHistorySavable(editable, history)
                  }
                >
                  {"저장하기"}
                </DSButton>
              </div>
            ) : null}
          </DSModal>
        );
    }
  },
);

const Filters = React.memo(
  ({
    startDate,
    endDate,
    locationFilter,
    onLocationChange,
    getLocations,
    storeLocations,
    onStartDateChange,
    onEndDateChange,
    hasInventoryAndAccess,
    onRetrievePress,
    isGroupView,
    typeFilter,
    onTypeFilterChange,
    hasCafe24Inventory,
    onSearchClear,
    onDateShortPress,
  }) => {
    let locations = getLocations(storeLocations);
    const reasons = (getInventoryReasonOptions(hasCafe24Inventory) || []).map(
      (r) => ({ text: r.label, value: r.value }),
    );
    return (
      <div className="ds-filter-card">
        <div className="ds-filter-row">
          <div className="ds-filter-row__body">
            <div className="ds-date-range">
              <DSDateInput
                label="시작일"
                value={startDate ? moment(startDate).toDate() : null}
                onChange={(date) =>
                  onStartDateChange({
                    target: {
                      value: date ? moment.utc(date).format("YYYY-MM-DD") : "",
                    },
                  })
                }
              />
              <span className="ds-date-range__sep">~</span>
              <DSDateInput
                label="종료일"
                value={endDate ? moment(endDate).toDate() : null}
                onChange={(date) =>
                  onEndDateChange({
                    target: {
                      value: date ? moment.utc(date).format("YYYY-MM-DD") : "",
                    },
                  })
                }
              />
            </div>
            <DSQuickDateGroup
              options={PeriodOptions.map((option) => ({
                value: option.key,
                label: option.text,
              }))}
              activeValue={null}
              onChange={onDateShortPress}
            />
          </div>
        </div>
        <div className="ds-filter-row">
          <div className="ds-filter-row__body">
            {!hasInventoryAndAccess ? null : (
              <DSDropdown
                multi
                options={locations}
                value={locationFilter || []}
                onChange={(value) => onLocationChange(null, { value })}
                placeholder="지점 선택"
                searchPlaceholder="지점 검색"
                multiLabelFormatter={(count) => `${count}개 지점 선택`}
                style={{ minWidth: 215 }}
              />
            )}
            {!isGroupView ? (
              <DSDropdown
                searchable
                options={[
                  {
                    text: "선택안함",
                    value: null,
                  },
                  ...reasons,
                ]}
                value={typeFilter}
                onChange={(value) => onTypeFilterChange(null, { value })}
                placeholder="조정 타입"
                style={{ minWidth: 183 }}
              />
            ) : null}
          </div>
        </div>
        <div className="ds-filter-actions">
          <DSButton variant="primary" onClick={onRetrievePress}>
            {"검색하기"}
          </DSButton>
          <DSButton variant="secondary" onClick={onSearchClear}>
            {"초기화"}
          </DSButton>
        </div>
      </div>
    );
  },
);

const INIT_DATES = [new Date().setDate(new Date().getDate() - 14), new Date()];

class StockHistories extends React.Component {
  constructor(props) {
    super(props);
    const _initDates = _.cloneDeep(INIT_DATES);
    this.state = {
      startDate: _initDates[0],
      endDate: _initDates[1],
      locationFilter: [],
      typeFilter: null,
      skuSearch: "",
      isSearched: false,
      page: 1,
      historyModal: false,
      selectedHistory: null,
      isHistoryLoading: false,
      isGroupView: false,
      detailPage: 1,
      isGroupHistoryLoading: false,
      isPaging: false,
      dateLimitExcessModal: false,
      isUpdating: false,
      forceCost: null,
      forceQuantity: null,
      forceExpiryDate: null,
      isRefresh: false,
      isDownloadingExcel: false,
      sendMailModal: false,
      email: "",
      tempNote: "",
      reportDownloadModal: false,
      pendingReport: null,
      isReportRequesting: false,
    };
  }

  componentDidMount() {
    if (this.props.storeId) {
      this.props.getItems(this.props.storeId);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.storeId !== prevProps.storeId) {
      this.props.getItems(this.props.storeId);
    }
    if (
      this.state.isHistoryLoading &&
      prevProps.orderStatus?.isFetching &&
      !this.props.orderStatus.isFetching
    ) {
      this.setState({
        isHistoryLoading: false,
        historyModal: true,
      });
    }
    if (
      this.state.isGroupHistoryLoading &&
      prevProps.status.isFetching &&
      !this.props.status.isFetching
    ) {
      this.setState({
        isGroupHistoryLoading: false,
        historyModal: true,
      });
    }
    if (
      this.state.isHistoryLoading &&
      prevProps.purchaseOrderStatus.isFetching &&
      !this.props.purchaseOrderStatus.isFetching
    ) {
      this.setState({
        isHistoryLoading: false,
        historyModal: true,
      });
    }
    if (
      (this.state.isPaging || this.state.isLoading) &&
      prevProps.status.isFetching &&
      !this.props.status.isFetching
    ) {
      if (this.props.status.error) {
        alert("조회에 실패하였습니다. 잠시 후 다시 시도해 주세요.");
      }
      this.setState({
        isPaging: false,
        isLoading: false,
      });
    }
    if (
      this.state.isUpdating &&
      prevProps.status.isFetching &&
      !this.props.status.isFetching
    ) {
      if (this.props.status.error) {
        alert("수정에 실패하였습니다. 잠시 후 다시 시도해 주세요.");
      } else {
        this.initHistoryModal();
        this.refreshHistory();
      }
      this.initUpdating();
    }
    if (
      this.state.isRefresh &&
      prevProps.status.isFetching &&
      !this.props.status.isFetching
    ) {
      this.initRefresh();
    }
    if (
      this.state.isDownloadingExcel &&
      prevProps.status.isFetching &&
      !this.props.status.isFetching
    ) {
      if (this.props.status.error) {
        alert("액샐 다운로드에 실패하였습니다. 잠시 후 다시 시도해 주세요.");
      }
      this.setState({ isDownloadingExcel: false });
    }
    if (prevProps.pdfStatus.isFetching && !this.props.pdfStatus.isFetching) {
      if (this.props.pdfStatus.error) {
        alert("PDF를 받는데 실패했습니다. 잠시 후 다시 시도해 주세요.");
      }
    }
  }

  initHistoryModal = () => {
    this.setState({
      isHistoryLoading: false,
      historyModal: false,
      forceCost: null,
      forceQuantity: null,
      forceExpiryDate: null,
      tempNote: "",
    });
  };

  initUpdating = () => {
    this.setState({
      isUpdating: false,
    });
  };

  initRefresh = () => {
    this.setState({
      isRefresh: false,
    });
  };

  handleLocationChange = (e, data) => {
    this.setState({ locationFilter: data.value });
  };

  getLocations = (locations) => {
    return (locations || [])
      .filter(
        (loc) =>
          loc.id === this.props.organization.locationId ||
          this.props.hasLocationAdminAccess,
      )
      .map((_loc) => {
        const isCafe24Location =
          _loc.id === this.props.cafe24SharedLocationId && !!_loc.name;
        return {
          ..._loc,
          value: _loc.id,
          text: _loc.name,
          content: isCafe24Location ? (
            <div className="stock-location-header-badge">
              <span>{_loc.name}</span>
              <div className="stock-location-header-badge__logo">
                <img src={cafe24LogoHorizontal} alt="Cafe24" />
              </div>
            </div>
          ) : (
            _loc.name
          ),
        };
      });
  };

  handleStartDateChange = (e) => {
    this.setState({
      startDate: e.target.value,
    });
  };

  handleEndDateChange = (e) => {
    this.setState({
      endDate: e.target.value,
    });
  };

  handleDateShortPress = (type) => {
    const { startDate, endDate } = getDateShortRange(type);
    this.setState({ startDate, endDate });
  };

  buildTypeFilters = (types = []) => {
    const hasCafe24Sales = (types || []).includes("cafe24sales");
    const reason = [
      ...new Set(
        (types || [])
          .map((t) => (t === "cafe24sales" ? "sales" : t))
          .filter(Boolean),
      ),
    ];
    const filters = [];
    if (reason.length > 0) {
      filters.push({
        key: "reason",
        value: reason,
        operation: "in",
      });
    }
    if (hasCafe24Sales) {
      filters.push({
        key: "referOrderOrigin",
        value: ["cafe24Sales"],
        operation: "in",
      });
    }
    return filters;
  };

  refreshHistory = () => {
    const { page, typeFilter, startDate, endDate, locationFilter } = this.state;
    const locationIds = this.isUsableLocationFilter()
      ? locationFilter
      : [this.props.currentLocationId];
    let filters = [];
    if ((locationIds || []).length > 0) {
      if (locationIds.length > 1) {
        filters.push({
          key: "locationId",
          value: locationIds,
          operation: "in",
        });
      } else {
        filters.push({
          key: "locationId",
          value: locationIds[0],
          operation: "eq",
        });
      }
    }
    filters = [
      ...filters,
      ...this.buildTypeFilters(typeFilter ? [typeFilter] : []),
    ];
    if (startDate && endDate) {
      let s = new Date(startDate);
      let e = new Date(endDate);
      s.setHours(0, 0, 0, 0);
      e.setHours(23, 59, 59, 999);
      filters.push({
        key: "createdAt",
        value: [s.toISOString(), e.toISOString()],
        operation: "between",
      });
    }
    this.setState({ isRefresh: true });
    this.props.refreshInventoryHistory(page, ITEM_PER_PAGE, "single", filters);
  };

  getInventoryHistories(
    page,
    isGroupView,
    dates,
    locationIds = null,
    search = null,
    reason = [],
    override = true,
  ) {
    let filters = [];
    if ((locationIds || []).length > 0) {
      if (locationIds.length > 1) {
        filters.push({
          key: "locationId",
          value: locationIds,
          operation: "in",
        });
      } else {
        filters.push({
          key: "locationId",
          value: locationIds[0],
          operation: "eq",
        });
      }
    }
    if (dates[0] && dates[1]) {
      let s = new Date(dates[0]);
      let e = new Date(dates[1]);
      s.setHours(0, 0, 0, 0);
      e.setHours(23, 59, 59, 999);
      const _s = new Date(s).toISOString();
      const _e = new Date(e).toISOString();
      filters.push({
        key: "createdAt",
        value: [_s, _e],
        operation: "between",
      });
    }
    if (search) {
      filters.push({
        key: "search",
        value: search.toLowerCase(),
        operation: "in",
      });
    }
    filters = [...filters, ...this.buildTypeFilters(reason)];
    if (page !== this.state.page) {
      this.setState({ page });
    }
    if (override) {
      this.setState({ isLoading: true });
    } else {
      this.setState({ isPaging: true });
    }
    this.props.getInventoryHistoriesByStore(
      page,
      ITEM_PER_PAGE,
      isGroupView ? "group" : "single",
      filters,
      override,
    );
  }

  getHistoryDetail(history, page, locationId = null, override = true) {
    let filters = [
      {
        key: "groupId",
        value: history.groupId,
        operation: "eq",
      },
    ];
    if (locationId) {
      filters.push({
        key: "locationId",
        value: locationId,
        operation: "eq",
      });
    }
    if (page !== this.state.detailPage) {
      this.setState({ detailPage: page });
    }
    if (!override) {
      this.setState({ isPaging: true });
    }
    this.props.getInventoryHistoriesByGroupId(
      this.props.histories[history.id],
      page,
      ITEM_PER_PAGE,
      filters,
    );
  }

  loadDetailMore = (history) => {
    this.getHistoryDetail(
      history,
      this.state.detailPage + 1,
      this.isUsableLocationFilter() ? null : this.props.currentLocationId,
      false,
    );
  };

  isStoreOrLocation() {
    if (Authority.level() === "location") {
      return { type: "location", id: this.props.currentLocationId };
    } else {
      return { type: "store", id: this.props.storeId };
    }
  }

  isUsableLocationFilter = () => {
    return (
      this.props.isMultiChannel && this.isStoreOrLocation().type === "store"
    );
  };

  handleRetrievePress = () => {
    let start = new Date(this.state.startDate);
    let end = new Date(this.state.endDate);
    if (isValidDate(start) && isValidDate(end)) {
      const diff = moment(end).diff(moment(start), "months");
      if (diff > Constants.REPORT_RESTRICTION_MONTH) {
        return this.setState({ dateLimitExcessModal: true });
      }
      this.getInventoryHistories(
        1,
        this.state.isGroupView,
        [start, end],
        this.isUsableLocationFilter()
          ? this.state.locationFilter
          : [this.props.currentLocationId],
        this.state.isSearched ? this.state.skuSearch : null,
        this.state.typeFilter ? [this.state.typeFilter] : [],
      );
      Analytics.sendMixPanelEvent("click", {
        screen: "StockHistories",
        action: "retrieve_history",
        detail: moment(end).diff(moment(start), "days"),
      });
    }
  };

  resetPeriod = () => {
    this.changePeriod("", new Date(), new Date());
  };

  changePeriod = (period, start, end) => {
    this.setState({ selectedPeriod: period });
    this.handlePeriodChange([start, end]);
  };

  handleEmailModalClose = () => {
    this.setState({
      sendMailModal: false,
      pendingReport: null,
    });
  };

  handleSendEmail = (email) => {
    if (this.state.pendingReport) {
      this.sendReportRequest(email);
      return;
    }
    this.exportCsv(true, email);
  };

  handleExportCsv = () => {
    this.exportCsv();
  };

  handleReportModalOpen = () => {
    this.setState({ reportDownloadModal: true });
  };

  handleReportModalClose = () => {
    this.setState({ reportDownloadModal: false });
  };

  // 모달 탭1: 조회한 히스토리 다운로드 — 기존 플로우(exportCsv, legacy 분기 포함)에 그대로 위임
  handleQueriedDownload = () => {
    this.setState({ reportDownloadModal: false });
    this.exportCsv();
  };

  // 모달 탭2·3: 리포트 요청 확정 → 이메일 확인 단계(기존 sendMailModal 플로우 재사용)로 진행
  handleReportRequest = (item, filters, reportName) => {
    this.setState({
      reportDownloadModal: false,
      pendingReport: { item, filters, reportName },
      sendMailModal: true,
    });
  };

  sendReportRequest = async (email) => {
    const { item, filters } = this.state.pendingReport;
    this.setState({
      sendMailModal: false,
      pendingReport: null,
      isReportRequesting: true,
    });
    const { status } = await requestReportDownload(
      this.props.storeId,
      item,
      filters,
      email,
    );
    this.setState({ isReportRequesting: false });
    if (status === REPORT_RESULT.EMAIL) {
      alert("데이터가 많아 파일이 준비되면 이메일로 발송됩니다.");
    } else if (status === REPORT_RESULT.ERROR) {
      alert("리포트 다운로드에 실패하였습니다.\n잠시 후 다시 시도해 주세요.");
    }
    Analytics.sendMixPanelEvent("click", {
      screen: "StockHistories",
      action: "download_list",
      detail: item,
    });
  };

  // 모달 탭1에 보여줄 현재 조회 조건 요약 (표시용 — 실제 다운로드 조건은 exportCsv가 다시 계산)
  getQueriedSummary = () => {
    const {
      startDate,
      endDate,
      locationFilter,
      typeFilter,
      skuSearch,
      isGroupView,
    } = this.state;
    const locationIds = this.isUsableLocationFilter()
      ? locationFilter
      : [this.props.currentLocationId];
    const locationNames = (locationIds || [])
      .map((id) => this.props.locationsById[id]?.name)
      .filter(Boolean);
    const reasonOption = getInventoryReasonOptions(
      this.props.hasCafe24Inventory,
    ).find((option) => option.value === typeFilter);
    return {
      period:
        startDate && endDate
          ? `${moment(startDate).format("YYYY/MM/DD")} ~ ${moment(
              endDate,
            ).format("YYYY/MM/DD")}`
          : "-",
      location: locationNames.length > 0 ? locationNames.join(", ") : "전체 지점",
      type: reasonOption?.label || "전체",
      search: skuSearch || null,
      view: isGroupView ? "그룹 히스토리" : "단일 히스토리",
    };
  };

  exportCsv = (emailValidated = false, email) => {
    const {
      typeFilter,
      isGroupView,
      skuSearch,
      startDate,
      endDate,
      locationFilter,
    } = this.state;
    let filters = [];
    const locationIds = this.isUsableLocationFilter()
      ? locationFilter
      : [this.props.currentLocationId];
    if ((locationIds || []).length > 0) {
      if (locationIds.length > 1) {
        filters.push({
          key: "locationId",
          value: locationIds,
          operation: "in",
        });
      } else {
        filters.push({
          key: "locationId",
          value: locationIds[0],
          operation: "eq",
        });
      }
    }
    filters = [
      ...filters,
      ...this.buildTypeFilters(typeFilter ? [typeFilter] : []),
    ];
    if (startDate && endDate) {
      let s = new Date(startDate);
      let e = new Date(endDate);
      s.setHours(0, 0, 0, 0);
      e.setHours(23, 59, 59, 999);
      filters.push({
        key: "createdAt",
        value: [s.toISOString(), e.toISOString()],
        operation: "between",
      });
    }
    if (skuSearch) {
      filters.push({
        key: "search",
        value: skuSearch.toLowerCase(),
        operation: "in",
      });
    }
    const historyType = isGroupView ? "group" : "single";
    if (
      isLegacyInventoryHistories(new Date(startDate).toISOString(), historyType)
    ) {
      this.setState({ isDownloadingExcel: true });
      this.props.downloadInventoryHistories(historyType, filters);
    } else {
      if (emailValidated) {
        API.sendReportToEmail(
          this.props.storeId,
          `${historyType}InventoryHistories`,
          filters,
          email,
        );
        this.handleEmailModalClose();
      } else {
        this.setState({ sendMailModal: true });
      }
    }
    Analytics.sendMixPanelEvent("click", {
      screen: "StockHistories",
      action: "download_list",
    });
  };

  handleTypeFilterChange = (e, data) => {
    this.setState({ typeFilter: data.value });
    let start = new Date(this.state.startDate);
    let end = new Date(this.state.endDate);
    this.getInventoryHistories(
      1,
      this.state.isGroupView,
      [start, end],
      this.isUsableLocationFilter()
        ? this.state.locationFilter
        : [this.props.currentLocationId],
      this.state.isSearched ? this.state.skuSearch : null,
      data.value ? [data.value] : [],
    );
  };

  handleSkuSearchChange = (e) => {
    this.setState({ skuSearch: e.target.value });
  };

  handleKeyDownForSearch = (e) => {
    if (e.key === "Enter") {
      this.handleHistorySearch();
    }
  };

  handleHistorySearch = () => {
    this.setState({ isSearched: true });
    let start = new Date(this.state.startDate);
    let end = new Date(this.state.endDate);
    this.getInventoryHistories(
      1,
      false,
      [start, end],
      this.isUsableLocationFilter() ? null : [this.props.currentLocationId],
      this.state.skuSearch,
      this.state.typeFilter ? [this.state.typeFilter] : [],
    );
  };

  handleHistoryClear = () => {
    this.props.clearInventoryHistories();
  };

  handleSearchClear = () => {
    this.setState({ skuSearch: "", isSearched: false });
    let start = new Date(this.state.startDate);
    let end = new Date(this.state.endDate);
    this.getInventoryHistories(
      1,
      false,
      [start, end],
      this.isUsableLocationFilter() ? null : [this.props.currentLocationId],
      "",
      this.state.typeFilter ? [this.state.typeFilter] : [],
    );
  };

  getProdSkuName = (skuId) => {
    let sku = this.props.skusById[skuId];
    let product = this.props.productsById[sku?.parentId];
    if (!product) {
      return "삭제된 제품";
    }
    return `${product?.name} - ${sku?.name}`;
  };

  brushupDetailItems = (histories) => {
    let data = [];
    for (let history of histories || []) {
      const sku = this.props.skusById[history.skuId];
      const product = sku && this.props.productsById[sku?.parentId];
      data = [
        ...data,
        {
          prodName: product.name || "삭제된 제품",
          skuName: sku?.name || "",
          model: sku.model || "",
          unit: sku.unit || sku.barcode || "",
          reason: GetInventoryTransactionReason(
            history.reason,
            history.quantity,
            history.referOrderOrigin,
          ),
          originalType: history.reason,
          quantity: Number(history.quantity),
          locationId: history.locationId,
          cost: history.totalCost,
        },
      ];
    }
    return data;
  };

  brushupGroupHistories = (histories) => {
    let data = [];
    for (let history of histories || []) {
      data = [
        ...data,
        {
          id: history.id,
          createdAt: moment(history.createdAt).format("YYYY/MM/DD HH:mm"),
          location: this.props.locationsById[history.locationId]?.name,
          type: GetInventoryTransactionReason(
            history.reason,
            history.quantity,
            history.referOrderOrigin,
          ),
          skuLength: history.groupHistoryCount,
          locationId: history.locationId,
          totalCost: history.totalCost,
          note: history.note || "",
          isGroup: true,
          groupId: history.groupId,
          employee: this.getEmployeeName(history.employeeId),
        },
      ];
    }
    return data;
  };

  getEmployeeName = (_employeeId) => {
    const { employeesById } = this.props;
    return employeesById[_employeeId]
      ? employeesById[_employeeId].lastName +
          employeesById[_employeeId].firstName ||
          employeesById[_employeeId].nickname
      : "";
  };

  brushupHistories = (histories) => {
    let data = [];
    for (let history of histories || []) {
      let skuId = history.skuId;
      // NOTE: for legacy
      if (!skuId && history.isLegacy) {
        let mixedIds = history.id.split("sku_");
        skuId = "sku_" + mixedIds[1];
      }
      let sku = this.props.skusById[skuId] || {};
      const prod = (sku && this.props.productsById[sku?.parentId]) || {};
      data = [
        ...data,
        {
          createdAt: moment(history.createdAt).format("YYYY/MM/DD HH:mm"),
          prodName: !prod ? "삭제된 제품" : prod.name,
          skuName: sku.name ?? "삭제된 제품",
          model: sku.model,
          unit: sku.unit || sku.barcode,
          location: this.props.locationsById[history.locationId]?.name,
          type: GetInventoryTransactionReason(
            history.reason,
            history.quantity,
            history.referOrderOrigin,
          ),
          id: history.id,
          originalType: history.reason,
          quantity: Number(history.quantity),
          locationId: history.locationId,
          totalCost: history.totalCost,
          cost: history.cost,
          sku: sku,
          note: history.note || "",
          employee: this.getEmployeeName(history.employeeId),
          original: history,
          expiryDate: history.expiryDate
            ? moment(new Date(history.expiryDate)).format(
                Constants.MOMENT.DATE_FORMAT,
              )
            : "-",
          originExpiryDate: history.expiryDate ? history.expiryDate : null,
        },
      ];
    }
    return data;
  };

  // 더보기 → 페이지네이션 전환 (우진 지시 2026-07-15): 해당 페이지를 override 재조회
  handlePageChange = (page) => {
    const start = new Date(this.state.startDate);
    const end = new Date(this.state.endDate);
    this.getInventoryHistories(
      page,
      this.state.isGroupView,
      [start, end],
      this.isUsableLocationFilter()
        ? this.state.locationFilter
        : [this.props.currentLocationId],
      this.state.skuSearch,
      this.state.typeFilter ? [this.state.typeFilter] : [],
    );
  };

  handleHistoryClick = (history) => {
    if (
      (history.originalType === "sales" ||
        history.originalType === "returns" ||
        history.originalType === "exchange") &&
      history.original.referId
    ) {
      this.props.getOrder(history.original.referId);
      this.setState({
        isHistoryLoading: true,
        selectedHistory: history,
        tempNote: history.note,
      });
    } else if (this.state.isGroupView && history.isGroup) {
      this.getHistoryDetail(
        history,
        1,
        this.isUsableLocationFilter() ? null : this.props.currentLocationId,
      );
      this.setState({
        selectedHistory: history,
        isGroupHistoryLoading: true,
        tempNote: history.note,
      });
    } else if (
      history.originalType === "newStock" &&
      history.original.purchaseOrderId
    ) {
      this.setState({
        isHistoryLoading: true,
        selectedHistory: history,
        tempNote: history.note,
      });
      this.props.getSuppliers(
        this.props.storeId,
        this.isUsableLocationFilter() ? null : this.props.currentLocationId,
      );
      this.props.getPurchaseOrderById(history.original.purchaseOrderId);
    } else {
      this.setState({
        historyModal: true,
        selectedHistory: history,
        tempNote: history.note,
      });
    }
  };

  handleHistoryModalCancel = () => {
    this.setState({
      historyModal: false,
      selectedHistory: null,
      forceCost: null,
      forceExpiryDate: null,
      forceQuantity: null,
    });
  };

  handleGroupPress = () => {
    const isGroupView = !this.state.isGroupView;
    this.setState({ isGroupView: isGroupView, typeFilter: null });
    let start = new Date(this.state.startDate);
    let end = new Date(this.state.endDate);
    this.getInventoryHistories(
      1,
      isGroupView,
      [start, end],
      this.isUsableLocationFilter()
        ? this.state.locationFilter
        : [this.props.currentLocationId],
      this.state.isSearched ? this.state.skuSearch : null,
      this.state.typeFilter ? [this.state.typeFilter] : [],
    );
  };

  getStatusLabel = (status) => {
    switch (status) {
      case "pending":
        return "대기중";
      case "complete":
      default:
        return "처리완료";
    }
  };

  handleCloseDateLimitExcessModal = () => {
    this.setState({ dateLimitExcessModal: false });
  };

  handlePrintPress = (historyId) => {
    let data = {};
    const _history = this.props.histories[historyId] || {};

    data = {
      id: _history.id,
      createdAt: moment(_history.createdAt).format(
        Constants.MOMENT.DATETIME_FORMAT,
      ),
      skuLength: (_history.histories || []).length,
      quantity: _.sumBy(_history.histories || [], "quantity"),
      state: this.getStatusLabel(_history.status),
      note: _history.note,
      totalCost: numberToCurrency(_history.totalCost),
      location: this.props.locationsById[_history.locationId]?.name,
      employee: this.getEmployeeName(_history.employeeId),
      origin: {
        ..._history,
        items: _history.histories || [],
      },
    };
    this.props.shareGroupHistoryPdf(data, "bulk");
    Analytics.sendMixPanelEvent("click", {
      screen: "StockHistories",
      action: "download_pdf",
    });
  };

  getEditableState = (reason, cost, purchaseOrderId) => {
    const purchaseOrder = purchaseOrderId
      ? this.props.purchaseOrdersById[purchaseOrderId]
      : null;
    if (purchaseOrder) {
      if (purchaseOrder.status === "received") {
        return { quantity: true, cost: true, expiryDate: true };
      } else {
        return { quantity: false, cost: false, expiryDate: false };
      }
    } else {
      switch (reason) {
        case "newStock":
          return { quantity: true, cost: true, expiryDate: true };
        case "force":
          if (cost > 0) {
            return { quantity: false, cost: true, expiryDate: true };
          } else if (cost <= 0) {
            return { quantity: false, cost: false, expiryDate: false };
          }
          break;
        case "loss":
        case "remove":
        case "damaged":
        case "add":
          return { quantity: true, cost: false, expiryDate: false };
        case "transfer":
        default:
          return { quantity: false, cost: false, expiryDate: false };
      }
    }
  };

  handleForceSave = (historyId) => {
    const _history = this.props.histories[historyId];
    let { forceCost, forceQuantity, forceExpiryDate } = this.state;
    let cost = 0;
    if (!_.isNil(forceCost) && _.isNumber(Number(forceCost))) {
      cost = Number(_.cloneDeep(forceCost));
    } else if (!_.isNil(_history.cost) && _.isNumber(Number(_history.cost))) {
      cost = Number(_.cloneDeep(_history.cost));
    } else {
      if (_history.reason === "newStock") {
        cost = 0;
      } else if (_history.reason === "force") {
        const sku = this.props.skusById[_history.skuId];
        cost = sku.cost;
      } else {
        cost = null;
      }
    }
    const quantity = Number(forceQuantity) || Number(_history.quantity) || 0;
    let expiryDate = forceExpiryDate || _history.expiryDate || null;

    expiryDate = expiryDate
      ? new Date(expiryDate).setHours(23, 59, 59, 999)
      : null;
    this.props.updateInventoryHistory(
      historyId,
      quantity,
      cost,
      expiryDate ? new Date(expiryDate).toISOString() : null,
    );
    this.setState({ isUpdating: true });
  };

  handleForceCostChange = (e) => {
    const cost = Number(removeString(e.target.value));
    if (isAvailableAmount(cost) && cost >= 0) {
      this.setState({
        forceCost: cost.toString(),
      });
    }
  };

  handleHistoryNoteSave = () => {
    if (this.state.selectedHistory.id) {
      this.props.updateInventoryHistoryNote(
        this.state.selectedHistory.id,
        this.state.tempNote,
      );
      this.setState({ isUpdating: true });
    }
  };

  handleHistoryNoteChange = (e) => {
    this.setState({ tempNote: e.target.value });
  };

  getStockQuantity = (reason, quantity) => {
    if (reason === "damaged" || reason === "loss" || reason === "remove") {
      return -Number(quantity);
    }
    return Number(quantity);
  };

  handleForceQuantityChange = (e) => {
    const quantity = this.getStockQuantity(
      this.state.selectedHistory?.originalType,
      removeString(e.target.value),
    );
    if (isAvailableQuantity(quantity)) {
      this.setState({
        forceQuantity: quantity.toString(),
      });
    }
  };

  handleExpiryDateChange = (date) => {
    let expiryDate = new Date(date);
    expiryDate.setHours(23, 59, 59, 999);
    this.setState({
      forceExpiryDate: expiryDate.toISOString(),
    });
  };

  isHistorySavable = (editable, _history) => {
    const { forceQuantity, forceCost, forceExpiryDate } = this.state;
    let savable = false;
    if (editable.quantity || editable.cost || editable.expiryDate) {
      if (editable.quantity) {
        if (
          forceQuantity &&
          !(_history.originalType === "remove" && forceQuantity === "0") &&
          forceQuantity !== _history.quantity?.toString()
        ) {
          savable = true;
        }
      }
      if (editable.cost) {
        if (forceCost && forceCost !== _history.cost?.toString()) {
          savable = true;
        }
      }
      if (editable.expiryDate) {
        const _forceExpiryDate = moment(new Date(forceExpiryDate)).format(
          Constants.MOMENT.DATE_FORMAT,
        );
        if (forceExpiryDate && _forceExpiryDate !== _history.expiryDate) {
          savable = true;
        }
      }
    }
    return savable;
  };

  renderList = () => {
    let _histories = this.brushupHistories(Object.values(this.props.histories));
    let _groupHistories = this.brushupGroupHistories(
      Object.values(this.props.histories),
    );
    let totalPage = Math.ceil(this.props.historyCount / ITEM_PER_PAGE);
    let { isGroupView, isUpdating, forceCost, forceQuantity, forceExpiryDate } =
      this.state;
    return (
      <>
        {this.state.historyModal ? (
          <HistoryModal
            open={this.state.historyModal}
            onCancel={this.handleHistoryModalCancel}
            history={this.state.selectedHistory}
            isGroup={this.state.selectedHistory.isGroup}
            brushupDetailItems={this.brushupDetailItems}
            histories={
              this.props.histories[this.state.selectedHistory?.id]?.histories ||
              []
            }
            detailProps={{
              suppliersById: this.props.suppliersById,
              purchaseOrdersById: this.props.purchaseOrdersById,
              currentLocationId: this.props.currentLocationId,
              ordersById: this.props.ordersById,
            }}
            onPrintPress={this.handlePrintPress}
            isPrinting={this.props.pdfStatus.isFetching}
            loadMore={() =>
              this.loadDetailMore(
                this.props.histories[this.state.selectedHistory?.id],
              )
            }
            hasMoreItems={
              ITEM_PER_PAGE * this.state.detailPage <
              this.props.historyItemCount
            }
            isPaging={this.state.isPaging}
            getEditableState={this.getEditableState}
            onExpiryDateChange={this.handleExpiryDateChange}
            hasExpiry={this.props.inventoryConfig.expiryDateMgmt}
            onForceSave={this.handleForceSave}
            onQuantityChange={this.handleForceQuantityChange}
            onCostChange={this.handleForceCostChange}
            forceCost={forceCost}
            forceQuantity={forceQuantity}
            forceExpiryDate={forceExpiryDate}
            isLoading={isUpdating}
            isRetail={this.props.isRetail}
            isHistorySavable={this.isHistorySavable}
            onNoteSave={this.handleHistoryNoteSave}
            onNoteChange={this.handleHistoryNoteChange}
            tempNote={this.state.tempNote}
          />
        ) : null}
        {isGroupView ? (
          <HistoryTable
            items={_groupHistories}
            headers={TABLE_GROUP_HEADERS}
            onRowClick={this.handleHistoryClick}
          />
        ) : (
          <>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <DSSearchInput
                value={this.state.skuSearch}
                onChange={(value) =>
                  this.handleSkuSearchChange({ target: { value } })
                }
                onSubmit={this.handleHistorySearch}
                placeholder={"제품, 품목코드(SKU), 바코드 검색 가능"}
                style={{ flex: 1 }}
              />
              <DSButton
                variant="primary"
                size="sm"
                onClick={this.handleHistorySearch}
                disabled={!this.state.skuSearch}
              >
                {"검색하기"}
              </DSButton>
              {this.state.isSearched ? (
                <DSButton
                  variant="secondary"
                  size="sm"
                  onClick={this.handleSearchClear}
                >
                  {"초기화"}
                </DSButton>
              ) : null}
            </div>
            <HistoryTable
              items={_histories}
              headers={TABLE_HEADERS}
              onRowClick={this.handleHistoryClick}
            />
          </>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "8px 0",
          }}
        >
          <DSPagination
            page={this.state.page}
            totalPages={totalPage}
            onChange={this.handlePageChange}
          />
        </div>
      </>
    );
  };

  render() {
    return (
      <>
        {this.state.dateLimitExcessModal ? (
          <LimitExcessModal
            open={this.state.dateLimitExcessModal}
            onClose={this.handleCloseDateLimitExcessModal}
            title={"기간 조회는 최대 90일까지 가능합니다."}
            customTitleClass={"normal mt-mb-base"}
          />
        ) : null}
        {this.state.sendMailModal ? (
          <ReportMailModal
            open={this.state.sendMailModal}
            title={
              this.state.pendingReport
                ? `${this.state.pendingReport.reportName} 다운로드`
                : "히스토리 다운로드"
            }
            onClose={this.handleEmailModalClose}
            reportName={
              this.state.pendingReport
                ? this.state.pendingReport.reportName
                : "재고 히스토리"
            }
            description={
              this.state.pendingReport ? (
                <>
                  {"리포트를 받으실 이메일 주소를 입력해 주세요."}
                  <br />
                  {"데이터가 많은 경우 파일이 준비되면 이메일로 발송됩니다."}
                </>
              ) : undefined
            }
            onSendEmail={this.handleSendEmail}
          />
        ) : null}
        {this.state.reportDownloadModal ? (
          <StockReportDownloadModal
            open={this.state.reportDownloadModal}
            onClose={this.handleReportModalClose}
            onDownloadQueried={this.handleQueriedDownload}
            onRequestReport={this.handleReportRequest}
            showReportTabs={this.props.isRetail}
            locations={this.getLocations(this.props.locations)}
            lockedLocationId={
              // 전체관리자(hasLocationAdminAccess)만 지점 선택 가능,
              // 담당 지점만 있는 지점장은 자기 매장으로 고정 (리포트도 담당 매장만 다운로드).
              // getLocations 옵션 필터와 동일한 게이트를 사용해 옵션/잠금 기준을 일치시킨다.
              this.props.hasLocationAdminAccess
                ? null
                : this.props.currentLocationId
            }
            queriedSummary={this.getQueriedSummary()}
          />
        ) : null}
        <Filters
          startDate={this.state.startDate}
          endDate={this.state.endDate}
          onStartDateChange={this.handleStartDateChange}
          onEndDateChange={this.handleEndDateChange}
          locationFilter={this.state.locationFilter}
          onLocationChange={this.handleLocationChange}
          getLocations={this.getLocations}
          hasInventoryAndAccess={
            this.props.hasInventory && this.props.hasLocationAdminAccess
          }
          isGroupView={this.state.isGroupView}
          storeLocations={this.props.locations}
          onRetrievePress={this.handleRetrievePress}
          typeFilter={this.state.typeFilter}
          onTypeFilterChange={this.handleTypeFilterChange}
          hasCafe24Inventory={this.props.hasCafe24Inventory}
          onSearchClear={this.handleHistoryClear}
          onDateShortPress={this.handleDateShortPress}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 16,
          }}
        >
          <DSQuickDateGroup
            options={[
              { value: "single", label: "단일 히스토리" },
              { value: "group", label: "그룹 히스토리" },
            ]}
            activeValue={this.state.isGroupView ? "group" : "single"}
            onChange={(value) => {
              if ((value === "group") !== this.state.isGroupView) {
                this.handleGroupPress();
              }
            }}
          />
          <DSButton
            variant="secondary"
            onClick={this.handleReportModalOpen}
            disabled={
              this.state.isDownloadingExcel || this.state.isReportRequesting
            }
            icon={
              this.state.isDownloadingExcel || this.state.isReportRequesting ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : null
            }
          >
            {"다운로드"}
          </DSButton>
        </div>
        {(this.props.status.isFetching ||
          this.state.isHistoryLoading ||
          this.props.itemStatus.isFetching) &&
        !this.state.isPaging &&
        !this.state.isDownloadingExcel ? (
          <LoadingElement />
        ) : (
          this.renderList()
        )}
      </>
    );
  }
}

export default withRouter(
  connect(
    (state) => ({
      storeId: reducers.getStoreId(state),
      products: reducers.getProducts(state),
      locations: reducers.getInventoryLocations(state),
      organization: reducers.getCurrentOrganization(state),
      hasInventory: reducers.hasLocationInventory(state),
      orderStatus: reducers.getOrderStatus(state),
      ordersById: reducers.getOrdersById(state),
      productsById: reducers.getProductsById(state),
      skusById: reducers.getSkusById(state),
      locationsById: reducers.getInventoryLocationsById(state),
      currentLocationId: reducers.getCurrentLocationId(state),
      hasLocationAdminAccess: reducers.hasLocationAdminAccess(state),
      histories: reducers.getInventoryHistoriesById(state),
      historyCount: reducers.getInventoryHistoryCount(state),
      historyItemCount: reducers.getInventoryHistoryItemCount(state),
      status: reducers.getInventoryStatus(state),
      purchaseOrderStatus: reducers.getPurchaseOrderStatus(state),
      isMultiChannel: reducers.isMultiChannel(state),
      purchaseOrdersById: reducers.getPurchaseOrdersById(state),
      suppliersById: reducers.getSuppliersById(state),
      itemStatus: reducers.getItemStatus(state),
      employeesById: reducers.getEmployeesById(state),
      inventoryConfig: reducers.getStoreInventoryConfig(state),
      isRetail: reducers.isRetailMode(state),
      pdfStatus: reducers.getPdfStatus(state),
      hasCafe24Inventory: reducers.hasConnectCafe24Inventory(state),
      cafe24SharedLocationId: reducers.getCafe24SharedLocationId(state),
    }),
    {
      getItems,
      getInventoryHistoriesByStore,
      getOrder,
      getInventoryHistoriesByGroupId,
      getPurchaseOrderById,
      getSuppliers,
      shareHistoryPdf,
      clearInventoryHistories,
      updateInventoryHistory,
      refreshInventoryHistory,
      downloadInventoryHistories,
      shareGroupHistoryPdf,
      updateInventoryHistoryNote,
    },
  )(StockHistories),
);
